import Constants from 'expo-constants';
import * as Device from 'expo-device';
import * as Notifications from 'expo-notifications';
import { Platform } from 'react-native';
import { notificationsAPI } from './api';

Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge: true,
    shouldShowBanner: true,
    shouldShowList: true,
  }),
});

async function registerWebPushNotifications(): Promise<boolean> {
  if (globalThis.window === undefined || !('Notification' in globalThis)) {
    console.warn('Web push notifications not supported in this browser');
    return false;
  }

  const currentPermission = Notification.permission;

  if (currentPermission === 'granted') {
    return true;
  }

  if (currentPermission === 'denied') {
    console.warn('Web push notifications were denied by the user');
    return false;
  }

  const permission = await Notification.requestPermission();
  return permission === 'granted';
}

export async function registerForPushNotifications(): Promise<string | null> {
  if (Platform.OS === 'web') {
    const granted = await registerWebPushNotifications();
    if (__DEV__) {
      console.log(`Web notification permission: ${granted ? 'granted' : 'not granted'}`);
    }
    return null;
  }

  if (!Device.isDevice) {
    console.warn('Push notifications require a physical device');
    return null;
  }

  const { status: existingStatus } = await Notifications.getPermissionsAsync();
  let finalStatus = existingStatus;

  if (existingStatus !== 'granted') {
    const { status } = await Notifications.requestPermissionsAsync();
    finalStatus = status;
  }

  if (finalStatus !== 'granted') {
    console.warn('Push notification permission not granted');
    return null;
  }

  const projectId = Constants.expoConfig?.extra?.eas?.projectId;

  const tokenData = await Notifications.getExpoPushTokenAsync({
    projectId,
  });

  const token = tokenData.data;

  try {
    await notificationsAPI.registerDevice(token, Platform.OS, Device.modelName ?? undefined);
  } catch (error) {
    console.error('Failed to register push token with server:', error);
  }

  if (Platform.OS === 'android') {
    Notifications.setNotificationChannelAsync('default', {
      name: 'Default',
      importance: Notifications.AndroidImportance.MAX,
      vibrationPattern: [0, 250, 250, 250],
      lightColor: '#30E8C8',
    });
  }

  return token;
}

export function addNotificationReceivedListener(
  callback: (notification: Notifications.Notification) => void,
) {
  return Notifications.addNotificationReceivedListener(callback);
}

export function addNotificationResponseListener(
  callback: (response: Notifications.NotificationResponse) => void,
) {
  return Notifications.addNotificationResponseReceivedListener(callback);
}
